import styled from '@emotion/styled';
import type { ReactElement } from 'react';
import { memo, useCallback, useEffect, useState } from 'react';

import type { CountryCode } from '@hotjar/country-lib';
import { CreditCardDetailsLoader } from './CreditCardDetailsLoader';
import type { CallbackSuccess, CallbackResponse, CallbackFailure } from './hooks/useZuoraLibrary';
import { useZuoraLibrary } from './hooks/useZuoraLibrary';
import { useZuoraParameters } from './hooks/useZuoraParameters';
import { CALLBACK_STATES, emitCallbackEvents } from './utils/emitCallbackEvents';
import { handleErrorsWithMessageBus } from './utils/handleErrors';

interface VaultedResponse {
  refId: string;
}

type OnErrorResponse = {
  type: CALLBACK_STATES.failedLoad | CALLBACK_STATES.failedSubmission;
} & CallbackFailure;

interface CreditCardDetailsProps {
  customerId?: number;
  countryCode?: CountryCode;
  onSuccess(response: VaultedResponse): void;
  onError(response: OnErrorResponse): void;
}

const Container = styled.div`
  position: relative;
`;

const FormContainer = styled.div`
  min-height: 420px;

  /* 
    zuora thought it would be nice to inject some opinionated styles on the iframe element.
    I didn't agree!
  */
  .z_hppm_iframe {
    width: 100% !important;
    background-color: unset !important;
  }
`;

export const CreditCardDetails = memo(
  ({ customerId, countryCode, onSuccess, onError }: CreditCardDetailsProps): ReactElement => {
    const [loading, setLoading] = useState(true);
    const zuora = useZuoraLibrary();
    const parameters = useZuoraParameters({ countryCode, customerId });

    const handleCallbackEvents = useCallback(
      (state: CALLBACK_STATES, response: CallbackResponse) => {
        return state === CALLBACK_STATES.success
          ? onSuccess({ refId: (response as CallbackSuccess).refId })
          : onError({ type: state, ...response } as OnErrorResponse);
      },
      [onSuccess, onError],
    );

    useEffect(() => {
      if (!zuora || !parameters) {
        return undefined;
      }

      if (parameters instanceof Error) {
        const { message } = parameters;
        return onError({
          type: CALLBACK_STATES.failedLoad,
          success: false,
          errorCode: 'unknown',
          errorMessage: message,
          responseFrom: 'useZuoraParameters',
        });
      }

      /**
       * for now we display the banner errors inside of the iframe
       * TODO: consider moving this out and show the banner errors outside.
       * It will then be one less UI element that is out of sync with Design System changes
       */
      const handleErrors = handleErrorsWithMessageBus(zuora.sendErrorMessageToHpm);
      const callback = emitCallbackEvents(handleCallbackEvents);

      zuora.setEventHandler('onloadCallback', () => {
        setLoading(false);
      });

      zuora.renderWithErrorHandler(parameters, {}, callback, handleErrors);

      return undefined;
    }, [zuora, parameters, handleCallbackEvents, onError]);

    return (
      <Container>
        {/* the loader is overlayed on top of the form, because zuora needs to be able to see #zuora_payment to render the page */}
        {loading && <CreditCardDetailsLoader />}
        <FormContainer id="zuora_payment" />
      </Container>
    );
  },
  // we never want this component to re-render cause the iframe form will exceed its attempts limit and throw an error
  () => true,
);
